import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import '../parents/AllParents.css';

function EditParent() {
  const { parentId } = useParams();
  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');
  const { userInfo } = useAuth();

  const role = userInfo?.role;
  const is_single_user = userInfo?.is_single_user;
  const user_location_ids = userInfo?.user_location_ids || [];

  const EDIT_ROUTE_BASE = '/ouder/edit';

  const [userStatus, setUserStatus] = useState(null);
  const [userRole, setUserRole] = useState(null);

  const [allIds, setAllIds] = useState([]);
  const [parentExists, setParentExists] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [students, setStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [selectedRelationship, setSelectedRelationship] = useState('vader');
  const [studentSearch, setStudentSearch] = useState('');

  const [values, setValues] = useState({
    firstname: '',
    lastname: '',
    email: '',
    mobile: '',
    beroep: '',
    iscontactperson: false,
    location_id: '',
  });

  const [locations, setLocations] = useState([]);
  const [emailExists, setEmailExists] = useState(false);
  const [loading, setLoading] = useState(true);

  axios.defaults.withCredentials = true;

  const handleCancel = () => navigate('/ouders');

  const fetchStudentsForParent = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/student-parents/parent/${parentId}/students`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStudents(res.data.data || []);
    } catch (err) {
      console.error('Error fetching students for parent:', err);
    }
  }, [parentId, token]);

  const fetchAllStudents = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/students/allstudents`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAllStudents(res.data.Data || []);
    } catch (err) {
      console.error('Error fetching all students:', err);
    }
  }, [token]);

  const fetchUserLoginData = useCallback(
    async (email) => {
      if (!email) {
        setUserStatus('Geen waarde');
        setUserRole('Geen waarde');
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/auth/logindata/${email}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.status === 204) {
          setUserStatus('Geen waarde');
          setUserRole('Geen waarde');
        } else if (response.status === 200) {
          setUserStatus(response.data.status || 'Geen waarde');
          setUserRole(response.data.role || 'Geen waarde');
        } else {
          setUserStatus('Error fetching data');
          setUserRole('Error fetching data');
        }
      } catch (error) {
        console.error('Error fetching user login data:', error);
        setUserStatus('Fout');
        setUserRole('Fout');
      }
    },
    [token]
  );

  const fetchAllIds = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/parent/allids`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const ids = (res.data.ids || []).map(String);
      setAllIds(ids);
    } catch (err) {
      console.error('Fout bij ophalen alle ouder-IDs:', err);
      setAllIds([]);
    }
  }, [token]);

  useEffect(() => {
    fetchAllIds();
  }, [fetchAllIds]);

  const currentIndex = useMemo(() => {
    if (!allIds.length) return -1;
    return allIds.indexOf(String(parentId));
  }, [allIds, parentId]);

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < allIds.length - 1;

  const goPrev = useCallback(() => {
    if (!hasPrev) return;
    const prevId = allIds[currentIndex - 1];
    navigate(`${EDIT_ROUTE_BASE}/${prevId}`);
  }, [hasPrev, allIds, currentIndex, navigate]);

  const goNext = useCallback(() => {
    if (!hasNext) return;
    const nextId = allIds[currentIndex + 1];
    navigate(`${EDIT_ROUTE_BASE}/${nextId}`);
  }, [hasNext, allIds, currentIndex, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        await fetchStudentsForParent();
        await fetchAllStudents();

        if (role === 'super_admin') {
          const locRes = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/location/allLocations`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          setLocations(locRes.data.data || []);
        } else {
          setLocations([]);
        }

        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/parent/getparentbyid/${parentId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const p = res.data.data;

        if (!p) {
          setParentExists(false);
          return;
        }

        setParentExists(true);

        setValues({
          firstname: p.firstname || '',
          lastname: p.lastname || '',
          email: p.email || '',
          mobile: p.mobile || '',
          beroep: p.beroep || '',
          iscontactperson: !!p.iscontactperson,
          location_id: p.location_id || '',
        });

        if (p.email) {
          await fetchUserLoginData(p.email);
        } else {
          setUserStatus(null);
          setUserRole(null);
        }

        if (p.location_id) {
          setSelectedLocations([p.location_id]);
        } else {
          setSelectedLocations([]);
        }
      } catch (err) {
        console.error('Error loading parent:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [parentId, role, token, fetchStudentsForParent, fetchAllStudents, fetchUserLoginData]);

  const handleEmailBlur = async () => {
    if (!values.email) return;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/auth/check-email-existence`,
        {
          params: { email: values.email },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setEmailExists(
        !!res.data.exists && String(res.data.existsParentId) !== String(parentId)
      );
    } catch (err) {
      console.error('Error checking email', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (emailExists) {
      alert('Dit e-mailadres is al in gebruik.');
      return;
    }

    setLoading(true);

    try {
      const payload = { ...values };

      if (role !== 'super_admin') {
        payload.location_id = user_location_ids[0] || null;
      }

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/parent/updateparent/${parentId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      navigate('/ouders');
    } catch (err) {
      console.error('Error updating parent', err);
      alert('Kon ouder niet bijwerken.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudent = async () => {
    if (!selectedStudentId) return;

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/student-parents/parent/add-student`,
        {
          parentId,
          studentId: selectedStudentId,
          relationship: selectedRelationship,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSelectedStudentId('');
      setSelectedRelationship('vader');
      setStudentSearch('');
      await fetchStudentsForParent();
    } catch (err) {
      console.error(err);
      alert('Kon leerling niet koppelen');
    }
  };

  const handleRemoveStudent = async (studentId) => {
    if (!window.confirm('Weet je zeker dat je deze leerling wilt ontkoppelen?')) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/student-parents/parent/remove-student`,
        {
          data: { parentId, studentId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      await fetchStudentsForParent();
    } catch (err) {
      console.error(err);
      alert('Kon leerling niet ontkoppelen');
    }
  };

  const checkAndDeactivateUser = async () => {
    try {
      const checkResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/auth/check-email-in-users?email=${values.email}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (checkResponse.data.exists) {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/auth/deactivate`,
          { email: values.email },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        await fetchUserLoginData(values.email);
      }
    } catch (error) {
      console.error('Error in checking email existence or deactivating user:', error);
    }
  };

  const checkAndActivateUser = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/auth/activate`,
        {
          name: `${values.firstname} ${values.lastname}`,
          email: values.email,
          role: 'ouder',
          location_ids: selectedLocations,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.Status === 'Success') {
        await fetchUserLoginData(values.email);
      } else {
        alert('Mogelijk is er iets misgegaan');
      }
    } catch (error) {
      console.error('Error in registerOrActivateUser:', error);
    }
  };

  const linkedStudentIds = useMemo(() => students.map((s) => s.student_id), [students]);

  const availableStudents = useMemo(() => {
    const term = studentSearch.toLowerCase().trim();

    return allStudents.filter((s) => {
      const fullName = `${s.firstname} ${s.lastname}`.toLowerCase();
      return !linkedStudentIds.includes(s.student_id) && (term === '' || fullName.includes(term));
    });
  }, [allStudents, linkedStudentIds, studentSearch]);

  if (loading) return <p>Loading...</p>;

  return (
    <div className="bg-white p-4 pb-xxl">
      <h2 className="text-center">Ouder Bewerken</h2>
      <hr className="separator" />

      <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={goPrev}
          disabled={!hasPrev}
          title={!hasPrev ? 'Geen vorige ouder' : ''}
        >
          <i className="bi bi-arrow-left"></i> Vorige
        </button>

        <span>
          Ouder {currentIndex >= 0 ? currentIndex + 1 : '?'} van {allIds.length}
        </span>

        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={goNext}
          disabled={!hasNext}
          title={!hasNext ? 'Geen volgende ouder' : ''}
        >
          Volgende <i className="bi bi-arrow-right"></i>
        </button>
      </div>

      {!parentExists && (
        <div className="alert alert-warning rounded-0">
          Ouder niet gevonden.
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="mb-3 col">
            <button
              type="button"
              className="btn btn-secondary w-50 rounded-0"
              onClick={checkAndActivateUser}
            >
              Enable inlog
            </button>
          </div>
          <div className="mb-3 col">
            <button
              type="button"
              className="btn btn-secondary w-50 rounded-0"
              onClick={checkAndDeactivateUser}
            >
              Disable inlog
            </button>
          </div>
        </div>

        <div className="mb-3 col">
          <p>Status: {userStatus ? userStatus : 'Loading...'}</p>
          <p>Role: {userRole ? userRole : 'Loading...'}</p>
        </div>

        <div className="row mb-3">
          <div className="col">
            <label>
              Voornaam <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control rounded-0"
              required
              value={values.firstname}
              onChange={(e) => setValues({ ...values, firstname: e.target.value })}
            />
          </div>
          <div className="col">
            <label>
              Achternaam <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control rounded-0"
              required
              value={values.lastname}
              onChange={(e) => setValues({ ...values, lastname: e.target.value })}
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col">
            <label>
              Email <span className="text-danger">*</span>
            </label>
            <input
              type="email"
              className="form-control rounded-0"
              required
              value={values.email}
              onChange={(e) => {
                setValues({ ...values, email: e.target.value });
                setEmailExists(false);
              }}
              onBlur={handleEmailBlur}
            />
            {emailExists && <div className="text-danger">Email is al in gebruik.</div>}
          </div>
          <div className="col">
            <label>Mobiel</label>
            <input
              type="text"
              className="form-control rounded-0"
              value={values.mobile}
              onChange={(e) => setValues({ ...values, mobile: e.target.value })}
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col">
            <label>Opleidingsniveau</label>
            <input
              type="text"
              className="form-control rounded-0"
              value={values.beroep}
              onChange={(e) => setValues({ ...values, beroep: e.target.value })}
            />
          </div>

          <div className="col d-flex align-items-end">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={values.iscontactperson}
                onChange={(e) => setValues({ ...values, iscontactperson: e.target.checked })}
              />
              <label className="form-check-label">Contactpersoon</label>
            </div>
          </div>
        </div>

        {!is_single_user && role === 'super_admin' && (
          <div className="mb-3">
            <label>Locatie</label>
            <select
              className="form-select rounded-0"
              value={values.location_id || ''}
              onChange={(e) => {
                const locId = e.target.value;
                setValues({ ...values, location_id: locId });

                if (locId) setSelectedLocations([locId]);
                else setSelectedLocations([]);
              }}
            >
              <option value="">-- Kies locatie --</option>
              {locations.map((loc) => (
                <option key={loc.location_id} value={loc.location_id}>
                  {loc.location_name}
                </option>
              ))}
            </select>
          </div>
        )}

        <hr className="separator" />
        <h5>Gekoppelde leerlingen</h5>

        <ul className="list-group mb-3">
          {students.length === 0 && (
            <li className="list-group-item text-muted">Geen gekoppelde leerlingen</li>
          )}

          {students.map((s) => (
            <li
              key={s.student_id}
              className="list-group-item d-flex justify-content-between align-items-center"
            >
              <span>
                {s.firstname} {s.lastname}
                {s.relationship ? ` (${s.relationship})` : ''}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => handleRemoveStudent(s.student_id)}
              >
                Ontkoppelen
              </button>
            </li>
          ))}
        </ul>

        <div className="row mb-2">
          <div className="col-md-5">
            <input
              type="text"
              className="form-control rounded-0"
              placeholder="Zoek leerling (voor- of achternaam)"
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-4">
            <select
              className="form-select rounded-0"
              value={selectedStudentId}
              onChange={(e) => setSelectedStudentId(e.target.value)}
            >
              <option value="">-- Selecteer leerling --</option>
              {availableStudents.map((s) => (
                <option key={s.student_id} value={s.student_id}>
                  {s.firstname} {s.lastname}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <select
              className="form-select rounded-0"
              value={selectedRelationship}
              onChange={(e) => setSelectedRelationship(e.target.value)}
            >
              <option value="vader">Vader</option>
              <option value="moeder">Moeder</option>
              <option value="voogd">Voogd</option>
              <option value="overig">Anders</option>
            </select>
          </div>

          <div className="col-md-3">
            <button
              type="button"
              className="btn btn-secondary w-100 rounded-0"
              disabled={!selectedStudentId}
              onClick={handleAddStudent}
            >
              Leerling koppelen
            </button>
          </div>
        </div>

        <hr className="separator" />
        <div className="row">
          <div className="col-md-6 mb-2">
            <button
              type="button"
              className="btn btn-secondary w-100 rounded-0"
              onClick={handleCancel}
            >
              Annuleren
            </button>
          </div>
          <div className="col-md-6">
            <button
              type="submit"
              className="btn btn-primary w-100 rounded-0"
              disabled={loading}
            >
              {loading ? 'Opslaan...' : 'Opslaan'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default EditParent;